import { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import { MdCheckCircle, MdError } from 'react-icons/md';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/Button';
import { showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import { activeSyncRequestAtom } from '../atoms/syncRequestAtom';
import useFetchSharedJobPostSyncRequest from '../hooks/useFetchSharedJobPostSyncRequest';

export default function SyncRequestWatcher() {
  const [activeSyncRequest, setActiveSyncRequest] = useAtom(activeSyncRequestAtom);
  const [resultOpen, setResultOpen] = useState(false);
  const queryClient = useQueryClient();

  const encryptId = activeSyncRequest?.encryptId ?? null;
  const { data: syncRequest, isError } = useFetchSharedJobPostSyncRequest(encryptId, {
    pollWhileStatusIn: ['processing'],
  });

  const status = syncRequest?.status;
  const isTerminal = status === 'completed' || status === 'failed';

  useEffect(() => {
    if (!encryptId || !isTerminal) return;
    setResultOpen(true);
    queryClient.invalidateQueries({ queryKey: ['/agent/api/shared_job_posts'] });
  }, [encryptId, isTerminal, queryClient]);

  // 別組織でのログイン等で取得できなくなったリクエストは追跡を打ち切る
  useEffect(() => {
    if (!encryptId || !isError || syncRequest) return;
    setActiveSyncRequest(null);
    showErrorToast('同期状況の取得に失敗しました');
  }, [encryptId, isError, syncRequest, setActiveSyncRequest]);

  const handleClose = () => {
    setResultOpen(false);
    setActiveSyncRequest(null);
  };

  if (!encryptId || !isTerminal) return null;

  return (
    <Dialog open={resultOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="w-[700px] max-w-[95vw] overflow-hidden rounded-sm shadow-layer3">
        <DialogHeader className="items-start space-y-xxs! px-lg! py-md!">
          <DialogTitle className="text-heading-xs text-main">
            {status === 'completed'
              ? '求人データベースの同期の完了'
              : '求人データベースの同期のエラー'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex h-[168px] flex-col items-center justify-center gap-md p-lg">
          {status === 'completed' ? (
            <MdCheckCircle className="size-[77px] text-primary" />
          ) : (
            <MdError className="size-[77px] text-danger" />
          )}
          <p className="text-body-sm text-main">
            {activeSyncRequest?.jobSourceTitle && (
              <span className="text-heading-xxs">{activeSyncRequest.jobSourceTitle} </span>
            )}
            {activeSyncRequest?.jobSourceTitle && 'との'}
            {status === 'completed' ? '同期が完了しました。' : '同期に失敗しました。'}
          </p>
        </div>
        <DialogFooter className="justify-end gap-md px-lg! py-md! sm:space-x-0!">
          <Button color="secondary" size="md" type="button" onClick={handleClose}>
            閉じる
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
